import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import {
  LoaderFunction,
  redirect,
  useLoaderData,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { useReferralRewardsFeature } from "@/modules/referralRewards";
import { ReferralRewardsCard } from "@/modules/referralRewards/ReferralRewardsCard/ReferralRewardsCard";
import { get, pget } from "api";
import { AffirmBanner } from "atoms/affirm/affirmBanner/AffirmBanner";
import { Banner } from "atoms/banner/Banner";
import Quiz from "atoms/icon/icons/ic_quiz_ol.svg";
import Discover from "atoms/icon/icons/offers_nav.svg";
import Star from "atoms/icon/icons/star.svg";
import { Notification, useNotify } from "atoms/notification";
import useDunningMembership from "hooks/useDunningMemberships";
import { useAdonisChat } from "modules/adonis/AdonisChatProvider/AdonisChatProvider";
import { AdvisorCTA } from "modules/adonis/AdvisorCTA/AdvisorCTA";
import { Analytics, AnalyticsEvents } from "modules/analytics";
import { useUserPreferences } from "modules/user/useUser/useUser";
import { AffirmModal } from "molecules/affirm-modal/AffirmModal";
import { BannersContainer } from "molecules/containers/banners-container/BannersContainer";
import CampaignCard from "organisms/campaign-card/CampaignCard";
import Carousel from "organisms/carousel-with-breadcrumb/Carousel";
import MembershipPromo from "organisms/memberships-promo/MembershipsPromo";
import ProductCarousel from "organisms/product-carousel/ProductCarousel";
import { ReferAFriend } from "organisms/refer-a-friend/ReferAFriend";
import BannerCTA from "organisms/shop/BannerCTA";
import ShopFeature from "organisms/shop/shop-feature/ShopFeature";
import PromotionHero from "organisms/shop/site-wide-promotion-hero/PromotionHero";
import ShowSiteWidePromotion from "organisms/site-wide-promotion-modal/SiteWidePromotionModal";
import { queryClient } from "pages/Root";
import { useFeatureFlag } from "services/Flags";
import { checkIfListingsHaveConcerns } from "services/HelperService";
import { getListingsWithDiscountInformation } from "services/ListingHelperService";
import { notFalsy } from "toolbox/Guards";
import { waitObj } from "toolbox/Promise";

import CategoriesSection from "./CategoriesSection";

const getReferralRewardCampaign = pget(
  "/referral-rewards/{platformId}/location/{locationId}"
);

const load = async () => {
  const platform = queryClient.fetchQuery(["/platform"], () =>
    get("/platform")
  );

  const shopConfig = queryClient.fetchQuery(["shop"], () => get("/shop"));

  const listings = queryClient.fetchQuery(
    ["/listings"],
    () => get("/listings"),
    { staleTime: 1000 * 60 }
  );

  const promotions = queryClient.fetchQuery(
    ["/promotions"],
    () => get("/promotions"),
    { staleTime: 1000 * 60 * 2 }
  );

  const memberships = queryClient.fetchQuery(["/memberships"], () =>
    get("/memberships")
  );
  const categories = queryClient.fetchQuery(["shop/categories"], () =>
    get("/shop/categories")
  );
  const customCategoriesList = queryClient.fetchQuery(
    ["/shop/custom-categories/listings"],
    () => get("/shop/custom-categories/listings"),
    { staleTime: 1000 * 10 }
  );
  const shopDiscounts = queryClient.fetchQuery(
    ["/shop/discounts"],
    () => get("/shop/discounts"),
    { staleTime: 1000 * 10 }
  );
  const user = queryClient.fetchQuery({
    queryKey: ["/user"],
    queryFn: () => get("/user"),
    staleTime: 1000 * 60 * 2,
  });

  const areRetailProductsVisible = queryClient.fetchQuery({
    queryKey: ["/retailproducts/are-retail-products-visible"],
    queryFn: () => get("/retailproducts/are-retail-products-visible"),
    staleTime: 1000 * 10,
  });

  const theme = queryClient.fetchQuery(["/platform/theme"], () =>
    get("/platform/theme")
  );

  return waitObj({
    platform,
    shopConfig,
    listings,
    promotions,
    memberships,
    categories,
    customCategoriesList,
    shopDiscounts,
    user,
    areRetailProductsVisible,
    theme,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const shopIndexLoader: LoaderFunction = async () => {
  const data = await load();
  if (!data.platform.platformSettings?.marketplaceEnabled) {
    return redirect("/home");
  }

  return data;
};

export const shopOnLoadNotificationTypes = {
  notFound: "not-found",
};

const ShopIndex = () => {
  const [searchParams] = useSearchParams();
  const initialNotificationType = searchParams.get("notification") || "";
  const navigate = useNavigate();
  const {
    platform,
    shopConfig,
    listings,
    promotions,
    categories,
    memberships,
    customCategoriesList,
    shopDiscounts,
    user,
    areRetailProductsVisible,
    theme,
  } = useLoaderData() as LoaderData;
  const flagSiteWidePromotions = useFeatureFlag("site-wide-promotions");
  const flagRepeatmdGlp = useFeatureFlag("repeatmd-glp");

  const flagRewardPatientsForReferringUsers = useFeatureFlag(
    "referral-rewards-phase-2"
  );

  const giftCardsFeatureFlagEnabled = useFeatureFlag("giftcards");
  const isV3Enabled = true;
  const adonisV3CTAText = useFeatureFlag("adonisV3CTAText");
  const {
    isPersonalisationEnabled,
    openChat,
    isEnabled: isAdonisEnabled,
  } = useAdonisChat();
  const { preferences } = useUserPreferences();

  const { data: giftCards } = useQuery({
    queryKey: ["/gift-cards"],
    queryFn: () => get("/gift-cards", {}),
    enabled: giftCardsFeatureFlagEnabled,
  });

  const giftCardsSettingEnabled =
    platform?.platformSettings?.giftCardEnabled ?? false;
  const isGiftCardsEnabled =
    giftCardsFeatureFlagEnabled && giftCardsSettingEnabled;

  const { data: activeCampaign } = useQuery({
    queryKey: getReferralRewardCampaign.keyed({
      platformId: platform?.id,
      locationId: user?.platformLocation?.id || "",
    }).queryKey,
    queryFn: () => {
      return getReferralRewardCampaign({
        platformId: platform?.id,
        locationId: user?.platformLocation?.id || "",
      });
    },
    select: (data) => data.filter((campaign) => campaign.isActive),
    enabled:
      !!platform?.id &&
      !!user?.platformLocation?.id &&
      flagRewardPatientsForReferringUsers,
  });

  const shopFeature = shopConfig?.shopFeatures?.[0];
  const membershipFeature = shopConfig?.membershipFeatures?.[0];
  const [showAffirmModal, setShowAffirmModal] = useState(false);
  const { dunningMembership } = useDunningMembership();
  const [notifyState, { info }] = useNotify();

  useEffect(() => {
    if (shopOnLoadNotificationTypes.notFound === initialNotificationType) {
      info("Sorry, we couldn't find the page you're looking for.", {
        timeout: 5000,
        dismissable: false,
      });
    }
  }, [initialNotificationType, info]);

  const sortedCustomCategoryPromotions = customCategoriesList
    .filter((listItem) => listItem.promotion !== null)
    .sort(
      (a, b) =>
        (a.promotion?.priority ?? 1) - (b.promotion?.priority ?? 1) ||
        dayjs(a.promotion?.expiresOn).valueOf() -
          dayjs(b.promotion?.expiresOn).valueOf()
    );

  const topSellingTreatmentCards = getListingsWithDiscountInformation(
    listings,
    customCategoriesList,
    shopDiscounts?.listingDiscounts
  )
    .filter(
      (listing) =>
        listing.type !== "Product" &&
        listing.type !== "ProductFromExternalSource"
    )
    .sort((a, b) => b.sellVolume - a.sellVolume)
    .map((item) => {
      if (!item.imageUrl || !item.title || !item.baseCost) {
        return null;
      }
      return {
        to: `/pdp/package/${item.id}`,
        imageSrc: item.imageUrl,
        tag: item.type,
        title: item.title,
        price: item.baseCost,
        memberPrice: item.membershipCost,
        discount: item.discountText,
        customColor: item.customColor,
      };
    })
    .filter(notFalsy)
    .slice(0, 5);

  const specialOffersCards = promotions
    .filter((promotion) => !promotion.redeemedOn)
    .map(
      (promotion) =>
        promotion && {
          to: user.isGuest ? "/signin" : "/discover?promotion=" + promotion?.id,
          tag: "Promotion",
          title: promotion.title,
          imageSrc: promotion.imageUrl ?? "",
          primary: user.isGuest ? "Sign up to use offer" : "View Offer",
          alert:
            promotion.expiresOn &&
            `Expires ${dayjs(promotion.expiresOn).fromNow()}`,
        }
    )
    .slice(0, 5);
  const marketplaceEnabled = platform.platformSettings?.marketplaceEnabled;

  const customCategoryToPromote = customCategoriesList[0];

  const bannerContainerShow = dunningMembership.status === false;

  const bannerAffirmShow = platform.platformSettings?.affirmEnabled;
  const bannerSitewidePromotionShow = !!customCategoryToPromote;

  const promotion = customCategoryToPromote?.promotion;
  const category = customCategoryToPromote?.category;
  const shouldShowCategoryPromotionInCarousel =
    flagSiteWidePromotions && promotion?.imageUrl != null && category;

  const browseByConcernImage = theme.resources?.find(
    (resource) => resource.key === "browse-by-concern-campaign"
  )?.url;

  const trackAdonisViewed = (): void => {
    Analytics.track(AnalyticsEvents.ADONIS_VIEWED, {});
  };

  const handleAdonisCTAClick = () => {
    openChat();
    trackAdonisViewed();
  };

  const shouldShowBrowseByConcern =
    !isAdonisEnabled &&
    !isPersonalisationEnabled &&
    checkIfListingsHaveConcerns(listings);

  const { showReferralRewards } = useReferralRewardsFeature();

  return (
    <>
      <Notification {...notifyState} />
      <AffirmModal
        platformLogo={platform.logoImageUrl ?? ""}
        open={showAffirmModal}
        onClose={() => setShowAffirmModal(false)}
        hideRewardText={!platform?.platformSettings?.rewardsEnabled}
      />
      {bannerContainerShow && (
        <BannersContainer>
          {bannerAffirmShow && (
            <AffirmBanner
              onClick={() => setShowAffirmModal(true)}
              hideRewardText={!platform?.platformSettings?.rewardsEnabled}
            />
          )}
          {flagSiteWidePromotions &&
            bannerSitewidePromotionShow &&
            promotion && (
              <Banner
                titleColor={promotion?.textColor ?? undefined}
                title={promotion?.title}
                backgroundColor={promotion?.backgroundColor ?? undefined}
                summary={promotion?.description ?? undefined}
                onClick={() =>
                  navigate(`/shop/custom-category/${category?.id}`)
                }
                arrow={true}
              ></Banner>
            )}
        </BannersContainer>
      )}
      {flagSiteWidePromotions && customCategoryToPromote?.promotion && (
        <ShowSiteWidePromotion
          promotion={customCategoryToPromote.promotion!}
          categoryId={customCategoryToPromote.category.id}
        />
      )}
      <Carousel
        showBreadCrumb={
          flagSiteWidePromotions &&
          promotion?.imageUrl != null &&
          (shopFeature ? 1 : 0) > 0
        }
      >
        {shouldShowCategoryPromotionInCarousel && (
          <div className="min-w-full snap-center" data-slideindex={0}>
            {promotion && (
              <PromotionHero {...promotion} categoryId={category.id} />
            )}
          </div>
        )}
        {flagRepeatmdGlp && (
          <div className="min-w-full snap-center" data-slideindex={1}>
            <BannerCTA
              title="Introducing Repeat Health"
              description="Medical weight loss"
              callToActionText="Take the quiz"
              navigateTo="/health/glp/questionnaire"
            />
          </div>
        )}
        {shopFeature && (
          <div
            className="min-w-full snap-center"
            data-slideindex={
              shouldShowCategoryPromotionInCarousel &&
              customCategoryToPromote?.promotion &&
              flagRepeatmdGlp
                ? 2
                : (shouldShowCategoryPromotionInCarousel &&
                    customCategoryToPromote?.promotion) ||
                  flagRepeatmdGlp
                ? 1
                : 0
            }
          >
            <ShopFeature
              {...shopFeature}
              showBreadCrumbs={
                flagSiteWidePromotions &&
                sortedCustomCategoryPromotions.length > 0
              }
            />
          </div>
        )}
      </Carousel>
      {marketplaceEnabled && membershipFeature && memberships?.length > 0 && (
        <MembershipPromo
          {...membershipFeature}
          memberships={memberships}
          useAnimatedBackground={isV3Enabled}
        />
      )}
      {isPersonalisationEnabled ? (
        <AdvisorCTA
          ctaText={adonisV3CTAText}
          onChatClick={handleAdonisCTAClick}
          showLink={
            preferences?.concernIds && preferences.concernIds.length > 0
          }
          onLinkClick={() => navigate("/shop/recommended-treatments")}
        />
      ) : (
        shouldShowBrowseByConcern && (
          <CampaignCard
            imageSrc={browseByConcernImage}
            title="Not sure what you need?"
            subtitle="Browse by concern"
            description="Explore our wide selection of effective treatment options, organized by specific concern, to address your unique skin needs and goals."
            svg={Quiz}
            primary="Search by concern"
            onButtonClick={() => navigate("/shop?concerns=true")}
          />
        )
      )}
      {listings.length > 0 && (
        <ProductCarousel
          svg={Star}
          title="best-selling services"
          cards={topSellingTreatmentCards}
          guestMode={user.isGuest}
        />
      )}
      <CategoriesSection
        affirmEnabled={platform.platformSettings?.affirmEnabled}
        categories={categories}
        customCategories={customCategoriesList}
        hasMemberships={memberships && memberships?.length > 0}
        hasTreatments={listings.some((listing) => listing.type === "Treatment")}
        hasPackages={listings.some((listing) => listing.type === "Package")}
        hasRetailProducts={
          !!areRetailProductsVisible &&
          listings.some(
            (listing) =>
              listing.type === "Product" ||
              listing.type === "ProductFromExternalSource"
          )
        }
        isGiftCardsEnabled={isGiftCardsEnabled}
        giftCards={giftCards}
      />
      {specialOffersCards.length > 0 && (
        <div className="bg-one">
          <ProductCarousel
            svg={Discover}
            title="special offers"
            cards={specialOffersCards}
          />
        </div>
      )}
      {showReferralRewards ? (
        <ReferralRewardsCard />
      ) : (
        <ReferAFriend withRepeatMDFooter />
      )}
    </>
  );
};

export default ShopIndex;
